/**
 * Network transport for fingerprint payloads.
 *
 * Two variants:
 *   - `postFingerprint(...)`: fetch() with 1 retry on transient failure
 *     (5xx / network error). Used by sdk.get() which needs the response.
 *   - `sendFingerprintBeacon(...)`: navigator.sendBeacon() for fire-and-forget
 *     delivery on pagehide / visibilitychange:hidden. Falls back to fetch()
 *     when sendBeacon is unavailable. Used by the lower-level sendFingerprint
 *     helper which doesn't consume the response.
 *
 * Continuity token storage helpers (read/persist) live here because they
 * pair with the transport flow — server sends a fresh continuity token in
 * the response, the client stores it and replays it on the next collect.
 */

const HELIOS_CONTINUITY_KEY = "_helios_continuity";

export interface ContinuityToken {
  visitor_id: string;
  issued_at: number;
  sig: string;
}

const isBrowser = typeof window !== "undefined";

export function readContinuityHintFromStorage(): ContinuityToken | undefined {
  if (!isBrowser) return undefined;
  try {
    const raw = localStorage.getItem(HELIOS_CONTINUITY_KEY);
    if (!raw) return undefined;
    const o = JSON.parse(raw) as Partial<ContinuityToken>;
    if (o?.visitor_id && typeof o.issued_at === "number" && o.sig) {
      return o as ContinuityToken;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

export function persistContinuityToken(token: ContinuityToken): void {
  if (!isBrowser) return;
  try {
    localStorage.setItem(HELIOS_CONTINUITY_KEY, JSON.stringify(token));
  } catch {
    /* ignore */
  }
}

export interface PostFingerprintOptions {
  endpoint: string;
  body: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Default 1 retry on 5xx / network error. 4xx fails immediately. */
  retries?: number;
  /** Backoff before retry, in ms. Default 500. */
  retryDelayMs?: number;
}

export interface PostFingerprintResponse {
  ok: boolean;
  status: number;
  text: string;
}

/**
 * POST a payload with retry on transient failure. 4xx responses fail
 * immediately (real errors); 5xx and network errors get one retry with
 * a 500ms backoff. The retry path eliminates the most common transient
 * failure mode (mobile cell handoff, brief packet loss).
 */
export async function postFingerprint(
  opts: PostFingerprintOptions,
): Promise<PostFingerprintResponse> {
  const retries = opts.retries ?? 1;
  const retryDelayMs = opts.retryDelayMs ?? 500;
  const headers = {
    "Content-Type": "application/json",
    ...opts.headers,
  };

  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(opts.endpoint, {
        method: "POST",
        headers,
        body: opts.body,
        signal: opts.signal,
        credentials: "omit",
        cache: "no-store",
      });
      const text = await response.text();

      // 4xx is a real error; don't retry.
      if (!response.ok && response.status >= 400 && response.status < 500) {
        return { ok: false, status: response.status, text };
      }

      // 5xx — retry once if we have attempts left.
      if (!response.ok && attempt < retries) {
        await sleep(retryDelayMs);
        continue;
      }

      return { ok: response.ok, status: response.status, text };
    } catch (err) {
      lastError = err;
      // AbortError should propagate immediately — caller cancelled.
      if ((err as { name?: string })?.name === "AbortError") {
        throw err;
      }
      if (attempt < retries) {
        await sleep(retryDelayMs);
        continue;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Network request failed");
}

/**
 * Fire-and-forget delivery via navigator.sendBeacon. Returns true if the
 * browser queued the beacon, false otherwise (caller can fall back to fetch).
 *
 * sendBeacon survives page-unload (pagehide / beforeunload / visibilitychange:
 * hidden) — fetch() during unload typically gets cancelled. This is the
 * standard transport for analytics-style fire-and-forget telemetry.
 */
export function sendFingerprintBeacon(endpoint: string, body: string): boolean {
  if (!isBrowser || typeof navigator.sendBeacon !== "function") {
    return false;
  }
  try {
    const blob = new Blob([body], { type: "application/json" });
    return navigator.sendBeacon(endpoint, blob);
  } catch {
    return false;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
